import * as React from "react";
import { Navigator } from "../../Shared/Wizards/Entities/Navigator";
import { Urls } from "../../Dashboard/Urls";
import { Dictionary } from "../../../react-core/Helpers/CommonHelper";
import { FormComponent } from "../../../react-core/Html/FormComponent";
import { RadiosEditor } from "../../../react-core/Html/RadiosEditor";
import { InsuredFundValidate } from "./Components/InsuredFundValidate";
import { InsuredFundArray, InsuredFundMap, ContentFilter } from "./Resource/Content";
import { UserClient } from "../../../Wrap/Api/User/User";
import { Table } from "../../Shared/Table/Table";
import { InsuredFundAction } from "./Actions/InsuredFundAction";
import { BulkTradeMode } from "../../../Wrap/Models/Models/Instruments/BulkTradeMode";
import { InsuredFundBulkTradeCommand } from "../../../Wrap/Models/Models/Instruments/InsuredFundBulkTradeCommand";
import { InsuredFundSwitchTo } from "./Components/InsuredFundSwitchTo";
import { TradeabilityStatus } from "../../../Wrap/Models/Models/Instruments/TradeabilityStatus";
import { Alert } from "../../Shared/Components/Alert";
import { AlertType } from "../../Shared/Entities/Constants/AlertConstants";
import { LoadingSkeletonType } from "../../Shared/LoadingSkeleton/Contents";
import { LoadingSkeletonWrapper } from "../../Shared/LoadingSkeleton/LoadingSkeletonWrapper";
import { InsuredFundBulkTradeResponse } from "../../../Wrap/Models/Models/Instruments/InsuredFundBulkTradeResponse";
import { JsonDateParser } from "../../../react-core/Utils/JsonDateParser";
import { UserStore } from "../../Dashboard/Stroes/Variable";
import { IndividualUserFunctionClient } from "../../../Wrap/Api/User/IndividualUserFunction";
import { checkPermission } from "../../Shared/Permission/PermissionHelper";
import { UserClaim } from "../../../Wrap/Constants/Enums/UserClaim";
import { Action } from "../../Dashboard/Actions/Action";

export type IInsuredFundWizardProps = React.Props<InsuredFundWizard>;

interface IInsuredFundWizardState {
    tableData: any;
    filter: any;
    type: BulkTradeMode;
    assetWatchStatus: number;
    assetWatchStatusSwitchTo: number;
    displayName: string;
    displayNameSwitchTo: string;
    userId?: number;
    isLoading: boolean;
    response: InsuredFundBulkTradeResponse;
    hasCanModifyInsuredFundsPermission: boolean;
}

export class InsuredFundWizard extends FormComponent<IInsuredFundWizardProps, IInsuredFundWizardState> {
    constructor(props: IInsuredFundWizardProps) {
        super(props);
        this.state = {
            tableData: null,
            filter: {},
            type: BulkTradeMode.BulkSell,
            assetWatchStatus: null,
            assetWatchStatusSwitchTo: null,
            displayName: null,
            displayNameSwitchTo: null,
            userId: null,
            isLoading: false,
            response: {},
            hasCanModifyInsuredFundsPermission: false
        };
    }

    componentDidMount() {
        Action.getUser((result) => {
            this.setState({ userId: result.ClientId });
            if (!checkPermission(UserClaim.AdviserPortalDisposeInsuredFundCanView)) {
                this.onExit();
            }
        });
        this.CanModifyInsuredFundsPermission();
    }

    public CanModifyInsuredFundsPermission() {
        const functionName = 'CanModifyInsuredFunds'
        const clientId = UserStore.getUserInfo().ClientId;

        if (clientId !== undefined && clientId != null) {
            const client = new IndividualUserFunctionClient();
            const promise = client.get(clientId, functionName);

            promise
                .onSuccess((data) => {
                    this.setState({ hasCanModifyInsuredFundsPermission : data });
                })
                .onError(() => {
                    this.setState({ hasCanModifyInsuredFundsPermission : false });
                })
        }
    }

    private renderIsValid(): JSX.Element {
        const { tableData, filter, assetWatchStatus, assetWatchStatusSwitchTo, response } = this.state;
        let responseMessage = [];
        response?.ErrorMessageList?.map((x, idx) => {
            responseMessage.push(x + " \r\n\n");
        });

        return (
            <>
                {tableData?.length == 0 && <Alert alertType={AlertType.Warning} message={"Insured fund not found."} />}
                {tableData?.some((x) => x.HasOpenOrders == true) && (
                    <Alert alertType={AlertType.Warning} message={"Insured fund Has Open Orders."} />
                )}
                {filter.FundCode &&
                    !(
                        assetWatchStatus == TradeabilityStatus.Tradeable ||
                        assetWatchStatus == TradeabilityStatus.SellTradeable
                    ) && <Alert alertType={AlertType.Warning} message={"Insured fund not Validate."} />}
                {filter.SwitchToInstrumentCode &&
                    !(
                        assetWatchStatusSwitchTo == TradeabilityStatus.Tradeable ||
                        assetWatchStatusSwitchTo == TradeabilityStatus.BuyTradeable
                    ) && <Alert alertType={AlertType.Warning} message={"Switch To Insured fund not Validate."} />}
                {response?.ErrorMessageList?.length == 0 && (
                    <Alert
                        alertType={AlertType.Success}
                        message={
                            "Success! This has been sent for authorisation and will require a second users to approval."
                        }
                    />
                )}
                {response?.ErrorMessageList?.length > 0 && (
                    <Alert alertType={AlertType.Error} message={<p>{responseMessage}</p>} />
                )}
            </>
        );
    }

    private renderTable(): JSX.Element {
        const { tableData } = this.state;
        if (!tableData) return null;

        return (
            <Table
                data={tableData}
                showEmptyMsg={true}
                col={[
                    {
                        title: "Account Name",
                        key: "AccountName",
                    },
                    {
                        title: "Account ID",
                        key: "ClientAccountId",
                    },
                    {
                        title: "Product",
                        key: "ProductDisplayName",
                    },
                    {
                        title: "Quantity",
                        key: "Quantity",
                    },
                    {
                        title: "Market Value(GBP)",
                        key: "MarketValue",
                    },
                    {
                        title: "Regular Investment",
                        key: "RegularPaymentAmount",
                    },
                    {
                        title: "Next Contribution Date",
                        key: "NextPayDate",
                        render: (data) => {
                            return (data.RegularPaymentAmount && data.RegularPaymentAmount > 0)?
                            JsonDateParser.getFormatDate(data.NextPayDate, "dd/MM/yyyy") : ""
                        }
                    },
                ]}
            />
        );
    }

    private renderSellSwitch(): JSX.Element {
        return (
            <RadiosEditor
                cssWrapper="radio-group rg-150 mb-23"
                options={InsuredFundArray}
                selected={this.state.type}
                getText={(v) => {
                    return InsuredFundMap[v];
                }}
                onChange={(v) => {
                    this.setState({ type: v });
                }}
                required="Please choose Insured Fund type"
            />
        );
    }

    private renderSwitchTo(): JSX.Element {
        const { filter } = this.state;
        return (
            <InsuredFundSwitchTo
                filter={filter}
                handleFilterChange={this.handleFilterChange}
                onSearchSwitchTo={this.onSearchSwitchTo}
                displayName={this.state.displayNameSwitchTo}
            />
        );
    }

    private renderValidate(): JSX.Element {
        const { filter } = this.state;
        return (
            <InsuredFundValidate
                filter={filter}
                handleFilterChange={this.handleFilterChange}
                onSearch={this.onSearch}
                displayName={this.state.displayName}
            />
        );
    }

    handleFilterChange = (changes: Dictionary[]) => {
        const updatedfilter = this.state.filter;
        for (const key in changes) {
            updatedfilter[key] = changes[key];

            if (key == "FundCode") {
                this.setState({ assetWatchStatus: null, tableData: null });
            }

            if (key == "SwitchToInstrumentCode") {
                this.setState({ assetWatchStatusSwitchTo: null });
            }
        }
        this.setState({ filter: updatedfilter });
    };

    onSearch = () => {
        const { filter, type } = this.state;
        const enableNpr = type == BulkTradeMode.BulkSwitch ? true : false;
        InsuredFundAction.getInsureFundDetails(filter?.FundCode, enableNpr, (data) => {
            this.setState({ assetWatchStatus: data.AssetWatchStatus, displayName: data?.DisplayName });
        });
    };

    onSearchSwitchTo = () => {
        const { filter, type } = this.state;
        const enableNpr = type == BulkTradeMode.BulkSwitch ? true : false;
        InsuredFundAction.getInsureFundDetails(filter?.SwitchToInstrumentCode, enableNpr, (data) => {
            this.setState({ assetWatchStatusSwitchTo: data?.AssetWatchStatus, displayNameSwitchTo: data?.DisplayName });
        });
    };

    onCancel() {
        this.onExit();
    }

    onExit() {
        Navigator.navigateTo(Urls.get().main.Admin);
    }

    private onClickSearch() {
        const { filter } = this.state;
        InsuredFundAction.getInsuredFundTradingAccount(filter?.FundCode, this.state.type, (data) => {
            this.setState({ tableData: data });
        });
    }

    private renderSearch(): JSX.Element {
        return (
            <>
                <div className="row">
                    <div className="col-12 col-sm-4">
                        <button
                            className="btn primary"
                            disabled={
                                !(
                                    this.state.assetWatchStatus == TradeabilityStatus.Tradeable ||
                                    this.state.assetWatchStatus == TradeabilityStatus.SellTradeable
                                )
                            }
                            onClick={() => this.onClickSearch()}
                        >
                            Search
                        </button>
                    </div>
                </div>
            </>
        );
    }

    private onClickSubmit() {
        const { filter, type, userId } = this.state;
        const insuredFundBulkTrade: InsuredFundBulkTradeCommand = {
            UserId: userId,
            SellInstrumentCode: filter?.FundCode,
            SwitchToInstrumentCode: filter?.SwitchToInstrumentCode,
            BulkTradeMode: type,
        };
        this.setState({ isLoading: true });
        InsuredFundAction.postInsuredFundBulkTrading(insuredFundBulkTrade, (callBack: InsuredFundBulkTradeResponse) => {
            this.setState({ isLoading: false, response: callBack });
        });
    }

    private renderSubmit(): JSX.Element {
        return (
            <>
                <div className="row">
                    <div className="col-12 col-right">
                        <button
                            className="btn primary"
                            disabled={this.disableSubmit()}
                            onClick={() => this.onClickSubmit()}
                        >
                            Confirm
                        </button>
                    </div>
                </div>
            </>
        );
    }

    private disableSubmit() {
        const { type } = this.state;

        if (!this.state.hasCanModifyInsuredFundsPermission) {
            return true;
        }

        if (type == BulkTradeMode.BulkSwitch) {
            return (
                !this.state.filter?.SwitchToInstrumentCode ||
                !this.state.filter?.FundCode ||
                !(
                    this.state.assetWatchStatusSwitchTo == TradeabilityStatus.Tradeable ||
                    this.state.assetWatchStatusSwitchTo == TradeabilityStatus.BuyTradeable
                ) ||
                this.state.filter?.FundCode == this.state.filter?.SwitchToInstrumentCode
            );
        }

        if (type == BulkTradeMode.BulkSell) {
            return (
                !this.state.filter?.FundCode ||
                !(
                    this.state.assetWatchStatus == TradeabilityStatus.Tradeable ||
                    this.state.assetWatchStatus == TradeabilityStatus.BuyTradeable
                )
            );
        }

        return false;
    }

    public render(): JSX.Element {
        return (
            <>
                <h1 className="page-heading">{ContentFilter.title}</h1>
                <LoadingSkeletonWrapper isLoading={this.state.isLoading} skeletonType={LoadingSkeletonType.Table}>
                    {this.renderIsValid()}
                    {this.renderValidate()}
                    <br />
                    {this.renderSellSwitch()}
                    {this.renderSearch()}
                    <br />
                    {this.renderTable()}
                    {this.state.type == BulkTradeMode.BulkSwitch && this.renderSwitchTo()}
                    {this.state.tableData && this.renderSubmit()}
                </LoadingSkeletonWrapper>
            </>
        );
    }
}
